<style scoped>
.map-wrap {
	position: relative;
	width: 100%;
	height: 400px;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.satellite-overlay {
	position: absolute;
	top: 10px;
	left: 10px;
	z-index: 1;
	display: flex;
	align-items: center;
	gap: 8px;
	background: rgba(255, 255, 255, 0.95);
	border-radius: 6px;
	padding: 6px 10px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
	font-size: 13px;
}

.satellite-overlay label {
	margin: 0;
	white-space: nowrap;
	font-weight: 500;
}

.satellite-overlay select {
	font-size: 13px;
	padding: 2px 6px;
}

.feature-card {
	margin-bottom: 1rem;
}

.feature-card .card-header {
	font-weight: 600;
	font-size: 0.95rem;
}

.nav-tabs .nav-link {
	font-size: 0.9rem;
	font-weight: 500;
}

.timeline-item {
	padding: 0.75rem 0;
	border-bottom: 1px solid #eee;
}

.timeline-item:last-child {
	border-bottom: none;
}

.timeline-date {
	font-size: 0.8rem;
	color: #6c757d;
	white-space: nowrap;
}

.badge-weather {
	font-size: 0.85rem;
}
</style>

<template>
	<div class="container-fluid py-4">
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			<p class="text-muted mt-2">Loading feature...</p>
		</div>

		<div v-else-if="error" class="alert alert-danger m-3">
			{{ error }}
		</div>

		<template v-else-if="detail">
			<div class="row mb-3">
				<div class="col">
					<h1 class="h3 mb-1">{{ headerLine }}</h1>
				</div>
			</div>

			<div class="row mb-3">
				<div class="col">
					<div class="map-wrap">
						<mgl-map
							:map-style="styleURL('alidade_satellite')"
							:center="center"
							:zoom="18"
							:bounds="mapBounds"
							:fit-bounds-options="{ padding: 40, duration: 800 }"
						>
							<mgl-geo-json-source
								v-if="municipalParcelsGeoJSON"
								source-id="municipal-parcels"
								:data="municipalParcelsGeoJSON"
							>
								<mgl-line-layer
									layer-id="municipal-parcels-outline"
									:paint="{
										'line-color': '#22c55e',
										'line-width': 2,
										'line-opacity': 0.8,
									}"
								/>
							</mgl-geo-json-source>
						</mgl-map>
						<div class="satellite-overlay">
							<label for="reveal-date">Reveal</label>
							<select
								id="reveal-date"
								v-model="selectedRevealDate"
								class="form-select form-select-sm"
							>
								<option value="">Default map</option>
								<option v-for="d in revealDates" :key="d" :value="d">
									{{ d }}
								</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<div class="row mb-3">
				<div class="col-md-6">
					<div class="card feature-card">
						<div class="card-header d-flex justify-content-between align-items-center">Feature Info <button type="button" class="btn btn-sm btn-outline-primary" @click="showPropertyDetails = true">Property Details</button></div>
						<div class="card-body">
							<dl class="row mb-0">
								<dt class="col-sm-4">Site</dt>
								<dd class="col-sm-8">
									<a :href="detail.site_uri">{{ siteLinkText }}</a>
								</dd>

								<dt class="col-sm-4">Type</dt>
								<dd class="col-sm-8">
									<span class="badge bg-primary">{{ featureTypeLabel(detail.type) }}</span>
								</dd>

								<dt class="col-sm-4">Condition</dt>
								<dd class="col-sm-8">
									<span
										class="badge"
										:class="conditionBadgeClass(detail.pool?.current_condition)"
									>
										{{ detail.pool?.current_condition ?? "Unknown" }}
									</span>
								</dd>

								<dt class="col-sm-4">Depth</dt>
								<dd class="col-sm-8">
									{{
										detail.pool?.depth_meters != null
											? detail.pool.depth_meters + " m"
											: "--"
									}}
								</dd>
							</dl>
						</div>
						<SitusParcelModal v-model="showPropertyDetails" :parcel="siteData?.parcel" :situs="siteData?.parcel?.situs" />
					</div>
				</div>

				<div class="col-md-6">
					<div class="card feature-card">
						<div class="card-header">Recent Weather</div>
						<div class="card-body">
							<dl class="row mb-0">
								<dt class="col-sm-5">Last rain</dt>
								<dd class="col-sm-7">
									<span class="badge-weather">3 days ago</span>
								</dd>

								<dt class="col-sm-5">Next rain</dt>
								<dd class="col-sm-7">
									<span class="badge-weather"
										>Tomorrow &mdash; 40% chance, ~0.2 in</span
									>
								</dd>
							</dl>
						</div>
					</div>
				</div>
			</div>

			<div class="row">
				<div class="col">
					<div class="card">
						<div class="card-header">
							<ul class="nav nav-tabs card-header-tabs">
								<li class="nav-item">
									<button
										class="nav-link"
										:class="{ active: activeTab === 'reveal' }"
										@click="activeTab = 'reveal'"
									>
										Satellite Imagery
									</button>
								</li>
								<li class="nav-item">
									<button
										class="nav-link"
										:class="{ active: activeTab === 'records' }"
										@click="activeTab = 'records'"
									>
										District Records
									</button>
								</li>
								<li class="nav-item">
									<button
										class="nav-link"
										:class="{ active: activeTab === 'interventions' }"
										@click="activeTab = 'interventions'"
									>
										Interventions
									</button>
								</li>
							</ul>
						</div>

						<div class="card-body" v-if="activeTab === 'reveal'">
							<p class="text-muted mb-2">
								Pool condition history from inspections and Nidus Reveal.
							</p>
							<table class="table table-sm table-hover mb-0">
								<thead>
									<tr>
										<th>Date</th>
										<th>Condition</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="entry in revealEntries"
										:key="entry.date"
										:class="{
											'table-active': selectedRevealDate === entry.date,
										}"
									>
										<td>{{ entry.date }}</td>
										<td>
											<span
												class="badge"
												:class="conditionBadgeClass(entry.condition)"
											>
												{{ entry.condition }}
											</span>
										</td>
										<td>
											<button
												class="btn btn-sm btn-outline-secondary"
												@click="selectedRevealDate = entry.date"
											>
												View on map
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div class="card-body" v-if="activeTab === 'records'">
							<p class="text-muted mb-2">
								District inspection records imported from CSV.
							</p>
							<table class="table table-sm table-hover mb-0">
								<thead>
									<tr>
										<th>Date</th>
										<th>Inspector</th>
										<th>Condition</th>
										<th>Notes</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="r in districtRecords" :key="r.id">
										<td class="timeline-date">{{ r.date }}</td>
										<td>{{ r.inspector }}</td>
										<td>
											<span
												class="badge"
												:class="conditionBadgeClass(r.condition)"
											>
												{{ r.condition }}
											</span>
										</td>
										<td>{{ r.notes }}</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div class="card-body" v-if="activeTab === 'interventions'">
							<p class="text-muted mb-2">Actions taken at this feature.</p>
							<div
								class="timeline-item"
								v-for="ix in interventions"
								:key="ix.id"
							>
								<div class="d-flex justify-content-between align-items-start">
									<div>
										<span
											class="badge me-2"
											:class="interventionBadgeClass(ix.type)"
										>
											{{ ix.type }}
										</span>
										{{ ix.description }}
									</div>
									<div class="timeline-date">{{ ix.date }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { MglMap, MglGeoJsonSource, MglLineLayer } from "vue-maplibre-gl";
import maplibregl from "maplibre-gl";
import SitusParcelModal from "@/components/SitusParcelModal.vue";
import { featureTypeLabel } from "@/lib/featureType";
import { mapStyleURL, boundsFromGeoJSON } from "@/map/util";
import { useFeatureStore } from "@/store/feature";
import { useStoreSite } from "@/store/site";
import type { Address, FeatureDetail, PoolStateEntry, Site } from "@/type/api";

interface Props {
	id: string;
}

const props = defineProps<Props>();
const featureStore = useFeatureStore();
const siteStore = useStoreSite();

const activeTab = ref<"reveal" | "records" | "interventions">("reveal");
const selectedRevealDate = ref("");
const showPropertyDetails = ref(false);

const detail = computed<FeatureDetail | null>(() => featureStore.detail);
const loading = computed(() => featureStore.loading);
const error = computed(() => featureStore.error);

const siteAddress = ref<Address | null>(null);
const siteData = ref<Site | null>(null);

onMounted(async () => {
	const feature = await featureStore.fetchByID(parseInt(props.id, 10));
	if (feature.site_id) {
		try {
			const site = await siteStore.fetchOne(feature.site_id);
			if (site) {
				siteData.value = site;
				if (site.address) {
					siteAddress.value = site.address;
				}
			}
		} catch {
			// site lookup is best-effort
		}
	}
});

function styleURL(name: string): string {
	return mapStyleURL(name);
}

const center = computed<[number, number]>(() => {
	const loc = detail.value?.location;
	if (loc?.latitude || loc?.longitude) {
		return [loc.longitude, loc.latitude];
	}
	const addr = siteAddress.value;
	if (addr?.location) {
		return [addr.location.longitude, addr.location.latitude];
	}
	return [-89.5, 39.7];
});

const mapBounds = computed<maplibregl.LngLatBounds | undefined>(() => {
	const gj = municipalParcelsGeoJSON.value;
	if (gj) {
		const b = boundsFromGeoJSON(gj, 10);
		if (b) return b;
	}
	return undefined;
});

const municipalParcelsGeoJSON = computed<GeoJSON.FeatureCollection | null>(
	() => {
		const parcel = siteData.value?.parcel;
		if (!parcel) return null;
		if (!parcel.geometry_geojson) return null;
		const features = [
			{
				type: "Feature" as const,
				properties: { id: parcel.id, external_id: parcel.external_id },
				geometry: parcel.geometry_geojson,
			},
		];
		if (features.length === 0) return null;
		return {
			type: "FeatureCollection" as const,
			features,
		};
	},
);

const headerLine = computed<string>(() => {
	const ft = detail.value;
	const t = ft ? featureTypeLabel(ft.type) : "Feature";
	const addr = siteAddress.value;
	if (addr) {
		const parts = [addr.number, addr.street, addr.locality].filter(Boolean);
		if (parts.length > 0) return `${t} at ${parts.join(" ")}`;
	}
	return `${t} #${props.id}`;
});

const siteLinkText = computed<string>(() => {
	const addr = siteAddress.value;
	if (addr) {
		const parts = [addr.number, addr.street, addr.locality, addr.region].filter(
			Boolean,
		);
		if (parts.length > 0) return parts.join(" ");
	}
	const ft = detail.value;
	return `Site #${ft?.site_id ?? "?"}`;
});

interface RevealEntry {
	date: string;
	condition: string;
}

const revealEntries = computed<RevealEntry[]>(() => {
	const states: PoolStateEntry[] = detail.value?.pool?.state_history ?? [];
	return states.map((s) => ({
		date: s.date.slice(0, 10),
		condition: s.condition,
	}));
});

const revealDates = computed<string[]>(() =>
	revealEntries.value.map((e) => e.date),
);

function conditionBadgeClass(condition: string | undefined): string {
	switch (condition) {
		case "blue":
			return "bg-primary";
		case "green":
			return "bg-success";
		case "dry":
			return "bg-secondary";
		case "covered":
			return "bg-danger";
		case "murky":
			return "bg-secondary";
		case "brown":
			return "bg-warning text-dark";
		default:
			return "bg-light text-dark";
	}
}

interface DistrictRecord {
	id: number;
	date: string;
	inspector: string;
	condition: string;
	notes: string;
}

const districtRecords: DistrictRecord[] = [
	{
		id: 1,
		date: "2026-06-28",
		inspector: "J. Smith",
		condition: "green",
		notes: "Algae visible, surface 60% covered",
	},
	{
		id: 2,
		date: "2026-05-15",
		inspector: "L. Chen",
		condition: "blue",
		notes: "Clear water, pump running",
	},
	{
		id: 3,
		date: "2026-04-02",
		inspector: "J. Smith",
		condition: "blue",
		notes: "Recently cleaned",
	},
	{
		id: 4,
		date: "2025-09-20",
		inspector: "M. Davis",
		condition: "dry",
		notes: "Drained for winter",
	},
	{
		id: 5,
		date: "2025-07-10",
		inspector: "L. Chen",
		condition: "green",
		notes: "Heavy algae bloom, mosquitos present",
	},
];

interface Intervention {
	id: number;
	date: string;
	type: string;
	description: string;
}

const interventions: Intervention[] = [
	{
		id: 1,
		date: "2026-07-05",
		type: "Mailer",
		description: "Compliance mailer sent to property owner",
	},
	{
		id: 2,
		date: "2026-06-15",
		type: "Visit",
		description: "Technician inspection — algae treatment applied",
	},
	{
		id: 3,
		date: "2026-05-20",
		type: "Fish",
		description: "200 mosquitofish stocked",
	},
	{
		id: 4,
		date: "2026-04-10",
		type: "Mailer",
		description: "Educational flyer mailed",
	},
];

function interventionBadgeClass(type: string): string {
	switch (type) {
		case "Visit":
			return "bg-primary";
		case "Mailer":
			return "bg-info text-dark";
		case "Fish":
			return "bg-success";
		default:
			return "bg-light text-dark";
	}
}
</script>
